import type { ViewerProfile } from "@wags/profile-contracts/profile.contract.ts";
import type { Answers } from "@wags/quiz-types/components/quiz/types.ts";
import {
    KNOWN_QUIZ_KEYS,
    QUIZ_PARAM_PREFIX,
    QUIZ_SCHEMA_VERSION,
    QUIZ_VERSION_PARAM,
    quizAnswerParamsSchema,
} from "../quiz/quiz.contract.ts";
import { getPrioritizedSelections } from "./priority-selections.ts";

export { QUIZ_SCHEMA_VERSION };

const NO_PREFERENCE = "no-preference";

function decodeAnswerArray(raw: string): string[] | null {
    const values: string[] = [];
    for (const encoded of raw.slice(2).split(",").filter(Boolean)) {
        try {
            values.push(decodeURIComponent(encoded));
        } catch {
            return null;
        }
    }
    return values;
}

export function mapQuizAnswersToUserProfile(answers: Answers): ViewerProfile {
    const profile: ViewerProfile = {};

    const values = (key: string): string[] => {
        const selections = getPrioritizedSelections(
            answers[key],
            NO_PREFERENCE,
        );
        return [...selections.preferred, ...selections.required];
    };

    const trainable = values("trainable");
    if (
        trainable.some(
            (value) =>
                value === "highly-trainable" || value === "easily-trainable",
        )
    ) {
        profile.lowTrainabilityTolerance = false;
    }

    const energy = values("energy")[0];
    if (energy) {
        const energyMap: Record<string, { target: number; weight: number }> = {
            "couch-buddy": { target: 0.1, weight: 0.5 },
            "low-energy": { target: 0.3, weight: 0.5 },
            moderate: { target: 0.5, weight: 0.4 },
            active: { target: 0.75, weight: 0.5 },
            "very-active": { target: 0.95, weight: 0.6 },
        };
        const spec = energyMap[energy];
        if (spec) {
            profile.targets = {
                ...(profile.targets ?? {}),
                energy: spec,
            };
        }
    }

    const sizes = values("size");
    if (sizes.length > 0) {
        const sizeMap: Record<string, number> = {
            toy: 0,
            small: 1,
            medium: 2,
            large: 3,
            giant: 4,
        };
        const buckets = sizes.flatMap((size) => {
            const bucket = sizeMap[size];
            return bucket === undefined ? [] : [bucket];
        });
        if (buckets.length > 0) {
            profile.sizeBuckets = buckets;
        }
    }

    if (values("shedding").includes("low")) {
        profile.lowShedding = true;
    }

    const family = values("family");
    if (
        family.some((value) =>
            ["kids-young", "kids-older", "planning"].includes(value),
        )
    ) {
        profile.hasKids = true;
        profile.requireKidFriendly = true;
    }

    return profile;
}

export function serializeQuizAnswersToParams(
    answers: Answers,
): Record<string, string> {
    const params: Record<string, string> = {
        [QUIZ_VERSION_PARAM]: String(QUIZ_SCHEMA_VERSION),
    };
    for (const key of KNOWN_QUIZ_KEYS) {
        const value = answers[key];
        if (typeof value === "string") {
            params[`${QUIZ_PARAM_PREFIX}${key}`] = value;
        } else if (Array.isArray(value)) {
            params[`${QUIZ_PARAM_PREFIX}${key}`] = `a:${value
                .map(encodeURIComponent)
                .join(",")}`;
        }
    }
    return params;
}

export function readQuizAnswersFromSearch(search: unknown): Answers | null {
    const parsed = quizAnswerParamsSchema.safeParse(search);
    if (!parsed.success) return null;
    if (Number(parsed.data[QUIZ_VERSION_PARAM]) !== QUIZ_SCHEMA_VERSION)
        return null;

    const answers: Answers = {};
    for (const key of KNOWN_QUIZ_KEYS) {
        const raw = parsed.data[`${QUIZ_PARAM_PREFIX}${key}`];
        if (typeof raw === "string") {
            const value = raw.startsWith("a:") ? decodeAnswerArray(raw) : raw;
            if (value === null) return null;
            answers[key] = value;
        }
    }
    return answers;
}

export function hasQuizAnswers(search: unknown): boolean {
    const answers = readQuizAnswersFromSearch(search);
    return answers != null && Object.keys(answers).length > 0;
}
